import type { Category } from '@scayle/storefront-nuxt'
import { computed, type ComputedRef, type Ref, toValue } from 'vue'
import { capitalCase } from 'change-case'
import { useRuntimeConfig } from '#app'
import { useI18n } from '#i18n'
import {
  type AllGenderMappingType,
  GENDER_MAPPING,
  type GenderMappingKeys,
} from '~/constants'
import { useRoute } from '#app/composables/router'
import { useHead } from '#imports'

function _normalizeString(string: string) {
  return string.normalize('NFKD').replace(/[^\w]/g, '')
}

function brandNameSameAsCategoryName(brand: string, category: Category) {
  const _brand = brand.toLocaleLowerCase()
  const _categoryName = category.name.toLocaleLowerCase()
  return _normalizeString(_brand) === _normalizeString(_categoryName)
}

function getCategoryGender(category: Category): AllGenderMappingType {
  const path = category.path.toLowerCase().split('/')
  const gender = path.find((keyword) =>
    Object.keys(GENDER_MAPPING).includes(keyword),
  )
  if (!gender) {
    return 'neutral'
  }
  return GENDER_MAPPING[gender as GenderMappingKeys]
}

export function usePLPSEO(
  categoryRef: Ref<Category | null>,
  brand: ComputedRef<string>,
) {
  const { t } = useI18n()
  const route = useRoute()
  const config = useRuntimeConfig()
  const shopName = config.public.shopName

  const _brand = toValue(brand)

  const pageNumber = computed(() => {
    const p = Number(route.query.page ?? 1)
    return Number.isFinite(p) && p > 0 ? p : 1
  })

  const metaTitle = computed(() => {
    const category = categoryRef.value

    if (!category) {
      return ''
    }

    let gender: AllGenderMappingType = getCategoryGender(category)

    // Override gender for special category types
    if (category.shopLevelCustomData.nonGenderCategory) {
      gender = 'neutral'
    }

    const brand = category.shopLevelCustomData.brand
      ? (category.shopLevelCustomData.brand as string).trim()
      : _brand.trim()
    const categoryName = category.name.trim()
    const brandName = brandNameSameAsCategoryName(brand, category)
      ? ''
      : capitalCase(brand)

    if (gender === 'neutral') {
      return t('product_list_page.meta.title_neutral_category', {
        brand: brandName,
        category: categoryName,
        count: pageNumber.value,
      })
    }

    if (gender === 'men') {
      return t('product_list_page.meta.title_men_category', {
        brand: capitalCase(brandName),
        category: capitalCase(categoryName),
        count: pageNumber.value,
      }).trim()
    }

    if (gender === 'women') {
      return t('product_list_page.meta.title_women_category', {
        brand: capitalCase(brandName),
        category: capitalCase(categoryName),
        count: pageNumber.value,
      }).trim()
    }

    if (gender === 'kids') {
      return t('product_list_page.meta.title_kids_category', {
        brand: capitalCase(brandName),
        category: capitalCase(categoryName),
        count: pageNumber.value,
      }).trim()
    }

    // fallback for any other gender
    return t('product_list_page.meta.title_neutral_category', {
      brand: capitalCase(brandName),
      category: capitalCase(categoryName),
      count: pageNumber.value,
    }).trim()
  })

  const metaDescription = computed(() => {
    const category = categoryRef.value

    if (!category) {
      return ''
    }

    let gender: AllGenderMappingType = getCategoryGender(category)

    if (category.shopLevelCustomData.nonGenderCategory) {
      gender = 'neutral'
    }

    const brand = category.shopLevelCustomData.brand
      ? (category.shopLevelCustomData.brand as string).trim()
      : _brand.trim()
    const categoryName = category.name.trim()
    const brandName = brandNameSameAsCategoryName(brand, category)
      ? ''
      : capitalCase(brand)

    if (gender === 'men') {
      return t('product_list_page.meta.description_men_category', {
        brand: capitalCase(brandName),
        category: capitalCase(categoryName),
        shopName: shopName,
      }).trim()
    }

    if (gender === 'women') {
      return t('product_list_page.meta.description_women_category', {
        brand: capitalCase(brandName),
        category: capitalCase(categoryName),
        shopName: shopName,
      }).trim()
    }

    if (gender === 'kids') {
      return t('product_list_page.meta.description_kids_category', {
        brand: capitalCase(brandName),
        category: capitalCase(categoryName),
        shopName: shopName,
      }).trim()
    }

    // neutral
    return t('product_list_page.meta.description_neutral_category', {
      brand: capitalCase(brandName),
      category: capitalCase(categoryName),
      shopName: shopName,
    }).trim()
  })

  useHead(() => {
    const meta: any[] = [{ property: 'og:title', content: metaTitle.value }]

    // Add description *only on first page*
    if (!pageNumber.value || pageNumber.value === 1) {
      meta.push({ name: 'description', content: metaDescription.value })
      meta.push({ property: 'og:description', content: metaDescription.value })
    }

    return {
      title: metaTitle.value,
      meta,
    }
  })

  return {
    metaTitle,
    metaDescription,
  }
}
